import { useTranslation } from 'react-i18next';
import {
  ComponentProps, ReactNode, useContext, useState,
} from 'react';
import { gql, useMutation } from '@apollo/client';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import _ from 'lodash';
import {
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  IconButton,
  Grid,
  ConfirmButtonColors,
  AccordionDetails,
} from '../../../..';
import { translateBackend } from '../../../../../assets/i18n/config';
import { WorkflowContext } from '../../workflowCompletion';
import { Account, AffiliationTypes } from '../../../../../interfaces';
import { useConfirmationDialog } from '../../components/useConfirmationDialog';
import { ReviewAccountSubstepOptions } from './reviewAccount.visual';
import { CustomFieldValueDisplay } from '../../components/customFieldValueDisplay';
import { Divider } from '../../../../2-component/divider/divider';
import { CustomFieldTypes } from '../../../../../interfaces/customField';

const DELETE_ACCOUNT = gql`
  mutation closeAccount($input: CloseAccountInput!) {
    closeAccount(input: $input) {
      account {
        id
      }
    }
  }
`;

const DELETE_SUB_ACCOUNT = gql`
  mutation closeSubAccount($input: CloseSubAccountInput!) {
    closeSubAccount(input: $input) {
      subAccount {
        id
      }
    }
  }
`;

const useAccountDeletion = (account: Account) => {
  const [deleteAccountMutation] = useMutation(DELETE_ACCOUNT, {
    variables: {
      input: {
        accountId: account.id,
        inactiveReason: 'OTHER',
      },
    },
  });
  const { setWorkflowData } = useContext(WorkflowContext);

  const [deleteSubAccountMutation] = useMutation(DELETE_SUB_ACCOUNT);

  const deleteAccount = async () => {
    for await (const subAccount of (account.subAccounts || [])) {
      await deleteSubAccountMutation({
        variables: {
          input: {
            subAccountId: subAccount.id,
            inactiveReason: 'OTHER',
          },
        },
      });
      await setWorkflowData((data) => ({
        ...data,
        currentSubAccountId: data.currentSubAccountId === subAccount.id ? undefined : data.currentSubAccountId,
        createdSubAccountIds: data.createdSubAccountIds?.filter((id) => id !== subAccount.id),
      }));
    }
    await deleteAccountMutation();
    await setWorkflowData((data) => ({
      ...data,
      currentAccountId: data.currentAccountId === account.id ? undefined : data.currentAccountId,
      createdAccounts: data.createdAccounts?.filter(({ id }) => id !== account.id),
    }));
  };

  return { deleteAccount };
};

const FieldDisplay = ({ title, children, expanded = true }: { title: string, children: ReactNode, expanded?: boolean }) => (
  <Grid item container xs={12} columnGap={2}>
    <Grid item xs={expanded ? 12 : 3}>
      <Typography variant='bodyMedium' sx={{ fontWeight: 600 }}>{title}</Typography>
    </Grid>
    <Grid item xs={expanded ? 12 : 7}>
      <Typography variant='bodyMedium' sx={{ fontWeight: 400 }}>{children}</Typography>
    </Grid>
  </Grid>
);

export const AccountCard = ({
  account,
  options,
  onAccountDeleted,
  onAccountEdit,
}: {
  account: Account,
  options: ReviewAccountSubstepOptions,
  onAccountDeleted: (accountId: string) => any,
  onAccountEdit: (accountId: string) => any,
}) => {
  const [expanded, setExpanded] = useState(false);
  const { t } = useTranslation(['workflowCompletions', 'accountTypes', 'affiliationTypes']);

  const { confirmationDialog, getUserConfirmation } = useConfirmationDialog();

  const accountDisplayTitle = account.translatedNickName ? translateBackend(account.translatedNickName) : t(`accountTypes:${account.type}`);

  const { deleteAccount } = useAccountDeletion(account);

  const onDeleteButtonClick: ComponentProps<typeof IconButton>['onClick'] = async (e) => {
    e.stopPropagation();
    const { closeDialog, confirmed } = await getUserConfirmation({
      title: t('reviewAccount.deleteAccountConfirmation.title', { accountDisplayTitle, accountId: `****${account.id.slice(-5)}` }),
      bodyText: t('reviewAccount.deleteAccountConfirmation.bodyText', { accountDisplayTitle, accountId: `****${account.id.slice(-5)}` }),
      setConfirmButtonColor: ConfirmButtonColors.destructive,
    });
    if (!confirmed) {
      closeDialog();
      return;
    }
    await deleteAccount();
    await onAccountDeleted(account.id);
    closeDialog();
  };

  const onEditButtonClick: ComponentProps<typeof IconButton>['onClick'] = async (e) => {
    e.stopPropagation();
    await onAccountEdit(account.id);
  };

  const affiliatesByTypes = _(account.affiliations || [])
    .groupBy((a) => a.type)
    .entries()
    .value();

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      variant='workflow'
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          borderTop: 'none !important',
          '.MuiAccordionSummary-content': { justifyContent: 'space-between !important' },
          '.MuiAccordionSummary-expandIconWrapper': { position: 'absolute', top: '10px', right: '15px' },
        }}
      >
        <Box width='100%'>
          <Box display='flex' justifyContent='space-between' >
            <Typography variant='bodyMedium' sx={{ fontWeight: 600 }}>
              {accountDisplayTitle} ****{account.id.slice(-5)}
            </Typography>
            <Box mr={3}>
              {options.enableAccountDelete && (
                <IconButton
                  size='xsmall'
                  onClick={onDeleteButtonClick}>
                  <DeleteIcon sx={{ fontSize: '15px' }} />
                </IconButton>
              )}
              {options.enableAccountEdit && (
                <IconButton
                  size='xsmall'
                  onClick={onEditButtonClick}>
                  <EditIcon sx={{ fontSize: '15px' }} />
                </IconButton>
              )}
            </Box>
          </Box>
          <Box mt={1}>
            <Divider />
            {!expanded && <Box width='100%' mt={2} display='block'>
              <Grid container rowGap={2}>
                {options.displayAccountType && (
                  <FieldDisplay expanded={false} title={t('workflowCompletions:reviewAccount.accountType')}>{t(`accountTypes:${account.type}`)}</FieldDisplay>
                )}
                {options.displayCustodian && (
                  <FieldDisplay expanded={false} title={t('workflowCompletions:reviewAccount.custodian')}>{account.custodianConnection?.name}</FieldDisplay>
                )}
              </Grid>
            </Box>}
          </Box>
        </Box>
      </AccordionSummary>

      <AccordionDetails>
        <Grid container rowGap={2}>
          {!(options.displayAccountType) ? null : (
            <FieldDisplay title={t('workflowCompletions:reviewAccount.accountType')}>{t(`accountTypes:${account.type}`)}</FieldDisplay>
          )}
          {!(options.displayCustodian) ? null : (
            <FieldDisplay title={t('workflowCompletions:reviewAccount.custodian')}>{account.custodianConnection?.name}</FieldDisplay>
          )}
          {!(options.displayRepcode && account.repCode?.name) ? null : (
            <FieldDisplay title={t('workflowCompletions:reviewAccount.repCode')}>{account.repCode?.name}</FieldDisplay>
          )}
          {!(options.displayAffiliates && account.affiliations?.length) ? null : (
            <>
              {affiliatesByTypes
                .filter(([affiliationType]) => affiliationType !== AffiliationTypes.Other)
                .map(([affiliationType, affiliations]) => (
                  <FieldDisplay title={t(`affiliationTypes:${affiliationType}`)}>
                    {affiliations.map((a) => (
                      <Box>{a.user.firstName} {a.user.lastName} ({t(`affiliationTypes:affiliationRelationship.${a.relation}`)})</Box>
                    ))}
                  </FieldDisplay>
                ))
              }
              {affiliatesByTypes
                .filter(([affiliationType]) => affiliationType === AffiliationTypes.Other)
                .map(([affiliationType, affiliations]) => (
                  <FieldDisplay title={t('reviewAccount.otherAffiliates')}>
                    {affiliations.map((a) => (
                      <Box>{a.user.firstName} {a.user.lastName} ({t(`affiliationTypes:affiliationRelationship.${a.relation}`)})</Box>
                    ))}
                  </FieldDisplay>
                ))
              }
            </>
          )}
          {!(options.displayLinkedGoal && account.subAccounts?.length) ? null : (
            <FieldDisplay title={t('reviewAccount.linkedGoals')}>
              {account.subAccounts?.map(({ goal }) => <Box>{goal.name || goal.type}</Box>)}
            </FieldDisplay>
          )}
          {
            !(options.displayCustomFields) ? null : account.customFields
              ?.filter((field) => field.customField?.type !== CustomFieldTypes.CUSTOM_OBJECT) /** @todo: remove this filter with https://onewealth.atlassian.net/browse/OV-19381 */
              ?.map((field) => (
                <FieldDisplay title={translateBackend(field.customField?.translatedName)} key={field.key}>
                  <CustomFieldValueDisplay field={field} />
                </FieldDisplay>
              ))
          }
        </Grid>
      </AccordionDetails>
      {confirmationDialog}
    </Accordion>
  );
};
